import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, computed, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { DocumentPreviewOverlayDirective } from '../../../../shared/directives/document-preview-overlay/document-preview-overlay.directive';
import { MaintenanceAllAttachmentsService } from './maintenance-all-attachments-service/maintenance-all-attachments.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { HttpStatusCode } from 'axios';
import { Router } from '@angular/router';
import { AttachmentFolder, AttachmentKind, MaintenanceAttachmentRow, MaintenanceStoredAttachment } from './maintenance-all-attachments-interfaces/maintenance-all-attachments.interface';

/** What the preview panel is showing. */
type PreviewMode = 'pdf' | 'image' | 'unsupported' | 'unavailable';
type KindFilter = 'all' | AttachmentKind;
type SortKey = 'Category' | 'Name' | 'Last modified' | 'Roles';

@Component({
  selector: 'app-maintenance-all-attachments',
  imports: [
    CommonModule,
    MatIcon,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
    DocumentPreviewOverlayDirective
  ],
  templateUrl: './maintenance-all-attachments.component.html',
  styleUrl: './maintenance-all-attachments.component.scss'
})
export class MaintenanceAllAttachmentsComponent implements OnInit, OnDestroy {

  form!: FormGroup;

  private readonly sanitizer = inject(DomSanitizer);

  /** Everything found, before any browsing filter is applied. */
  readonly rows = signal<MaintenanceAttachmentRow[]>([]);

  readonly query = signal('');
  /** Section title of the open folder, or `null` at the top level. */
  readonly openFolder = signal<string | null>(null);
  readonly kindFilter = signal<KindFilter>('all');
  readonly sortKey = signal<SortKey>('Last modified');
  readonly sortDir = signal<'asc' | 'desc'>('desc');
  /** Single-click highlights a file the way Drive does; double-click opens it. */
  readonly selectedFile = signal<MaintenanceAttachmentRow | null>(null);
  storedAttachments: MaintenanceStoredAttachment[] = [];

  readonly kindFilters: { value: KindFilter; label: string; icon: string }[] = [
    { value: 'all', label: 'All types', icon: 'filter_list' },
    { value: 'pdf', label: 'PDFs', icon: 'picture_as_pdf' },
    { value: 'image', label: 'Images', icon: 'image' },
    { value: 'other', label: 'Other files', icon: 'insert_drive_file' }
  ];

  readonly sortOptions: { value: SortKey; label: string }[] = [
    { value: 'Category', label: 'Category' },
    { value: 'Name', label: 'Name' },
    { value: 'Last modified', label: 'Last modified' },
    { value: 'Roles', label: 'Roles' },
  ];

  /** Rows left after search / type / status, ignoring which folder is open. */
  private readonly searchedRows = computed(() => {
    const term = this.query().trim().toLowerCase();
    const kind = this.kindFilter();

    return this.rows().filter((row) => {
      if (kind !== 'all' && row.kind !== kind) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        row.fileName.toLowerCase().includes(term) ||
        row.documentLabel.toLowerCase().includes(term) ||
        row.updatedByRole.toLocaleLowerCase().includes(term)
      );
    });
  });

  /** One folder per section that still has a matching document in it. */
  readonly folders = computed<AttachmentFolder[]>(() => {
    const byTitle = new Map<string, AttachmentFolder>();

    this.searchedRows().forEach((row) => {
      const folder = byTitle.get(row.updatedByRole) ?? {
        title: row.updatedByRole == 'NEWREP' ? 'APPLICANT' : row.updatedByRole,
        updatedByRole: row.updatedByRole,
        count: 0,
        sizeBytes: 0,
        pendingCount: 0
      };
      folder.count += 1;
      folder.sizeBytes += row.sizeBytes ?? 0;
      byTitle.set(row.updatedByRole, folder);
    });

    const order = this.storedAttachments.map((section) => section.updatedByRole);
    return [...byTitle.values()].sort((a, b) => {
      const rankA = order.indexOf(a.updatedByRole);
      const rankB = order.indexOf(b.updatedByRole);
      return (
        (rankA === -1 ? order.length : rankA) - (rankB === -1 ? order.length : rankB) ||
        a.title.localeCompare(b.title)
      );
    });
  });

  /** The files actually rendered — folder-scoped and sorted. */
  readonly visibleRows = computed(() => {
    const folder = this.openFolder();
    const scoped = folder
      ? this.searchedRows().filter((row) => row.updatedByRole === folder)
      : this.searchedRows();

    const key = this.sortKey();
    const direction = this.sortDir() === 'asc' ? 1 : -1;

    return [...scoped].sort((a, b) => {
      let comparison = 0;
      switch (key) {
        case 'Category':
          comparison = a.documentLabel.localeCompare(b.documentLabel);
          break;
        case 'Name':
          comparison = a.fileName.localeCompare(b.fileName);
          break;
        case 'Last modified':
          comparison = (a.uploadedAt?.getTime() ?? 0) - (b.uploadedAt?.getTime() ?? 0);
          break;
        case 'Roles':
          comparison = a.updatedByRole.localeCompare(b.updatedByRole);
          break;
      }
      return comparison * direction;
    });
  });

  /** True when there are documents, but the current search / filters hide them all. */
  readonly filteredToNothing = computed(
    () => this.rows().length > 0 && this.visibleRows().length === 0 && this.folders().length === 0
  );

  readonly hasActiveFilters = computed(
    () => !!this.query().trim() || this.kindFilter() !== 'all'
  );

  /** Display name of the open folder — the tiles show `APPLICANT` for `NEWREP`. */
  readonly openFolderTitle = computed(() => {
    const role = this.openFolder();
    if (!role) {
      return '';
    }
    return this.folders().find((folder) => folder.updatedByRole === role)?.title ?? role;
  });

  // ── Preview panel state (mirrors the new-rep-application preview) ──
  showPreview = false;
  previewMode: PreviewMode = 'unavailable';
  selectedDocument: SafeResourceUrl | null = null;
  currentFileName = '';
  /** Kept so the panel's Download button can act on the row being previewed. */
  previewRow: MaintenanceAttachmentRow | null = null;

  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 3;
  private readonly ZOOM_STEP = 0.25;
  zoomLevel = 1;

  /** Only object URLs this component created get revoked on close. */
  private createdObjectUrl: string | null = null;
  /** Thumbnail object URLs, released together when the component goes away. */
  private readonly thumbUrls: string[] = [];
  private maintenanceAllAttachmentsService = inject(MaintenanceAllAttachmentsService);
  setSprinner = signal(false);
  private snackBarService = inject(SnackbarService);
  private router = inject(Router);
  data = history.state;

  async ngOnInit(): Promise<void> {
    await this.fetchAllUploadedDocuments();

    this.rows.set([...this.storedRows()]);
  }

  async fetchAllUploadedDocuments() {
    this.setSprinner.set(true);
    let repCode = this.data.applicant.repcode;
    const responseData = await this.maintenanceAllAttachmentsService
      .getAllUploadDocuments(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.storedAttachments = responseData.data.responsedata;

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }


  }

  ngOnDestroy(): void {
    this.releaseObjectUrl();
    this.thumbUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  // ── Row building ───────────────────────────────────────────
  private storedRows(): MaintenanceAttachmentRow[] {
    return this.storedAttachments.map((stored) => ({
      documentLabel: stored.onboardingDocumentType,
      fileName: stored.fileName,
      uploadedAt: stored.uploadDate ? new Date(stored.uploadDate) : null,
      sizeBytes: stored.fileSizeBytes ?? null,
      updatedByRole: stored.updatedByRole,
      kind: this.kindOf(stored.fileName),
      documentId: stored.id,
      previewUrl: stored.previewUrl
    }));
  }

  /**
   * Every `File` sitting on the form, wherever it lives. Section order follows the
   * picker grid so the list reads the same way the sections do.
   */
  // private pendingRows(): MaintenanceAttachmentRow[] {
  //   if (!this.form) {
  //     return [];
  //   }

  //   const rows: MaintenanceAttachmentRow[] = [];

  //   const walk = (control: AbstractControl, groupName: string, controlName: string): void => {
  //     if (control instanceof FormGroup) {
  //       Object.keys(control.controls).forEach((key) =>
  //         walk(control.controls[key], groupName || key, groupName ? key : '')
  //       );
  //       return;
  //     }

  //     if (control instanceof FormArray) {
  //       control.controls.forEach((child) => walk(child, groupName, controlName));
  //       return;
  //     }

  //     const value = control.value;
  //     if (!(value instanceof File)) {
  //       return;
  //     }

  //     const kind = this.kindOf(value.name);

  //     rows.push({
  //       documentLabel: this.humanise(controlName),
  //       fileName: value.name,
  //       sectionTitle: this.sectionTitle(groupName),
  //       // A File only knows when it was last modified on disk; nothing has been
  //       // uploaded yet, so that stands in as the date until the row is saved.
  //       uploadedAt: value.lastModified ? new Date(value.lastModified) : null,
  //       sizeBytes: value.size,
  //       updatedByRole: '',
  //       kind,
  //       file: value,
  //       thumbUrl: kind === 'image' ? this.thumbnailFor(value) : undefined
  //     });
  //   };

  //   Object.keys(this.form.controls).forEach((key) => walk(this.form.controls[key], key, ''));

  //   const order = this.sections.map((section) => section.id);
  //   const rank = (row: MaintenanceAttachmentRow) => {
  //     const index = this.sections.findIndex((section) => section.title === row.sectionTitle);
  //     return index === -1 ? order.length : index;
  //   };

  //   return rows.sort((a, b) => rank(a) - rank(b) || a.documentLabel.localeCompare(b.documentLabel));
  // }

  /** Image tiles preview the picture itself; the URL lives until the view is torn down. */
  // private thumbnailFor(file: File): string {
  //   const url = URL.createObjectURL(file);
  //   this.thumbUrls.push(url);
  //   return url;
  // }


  // /** `managerEmailAttachment` → `Manager Email Attachment`. */
  // private humanise(name: string): string {
  //   if (!name) {
  //     return '';
  //   }
  //   const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  //   return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  // }


  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.selectedFile.set(null);
  }

  clearQuery(): void {
    this.query.set('');
  }

  setKindFilter(kind: KindFilter): void {
    this.kindFilter.set(kind);
    this.selectedFile.set(null);
  }

  clearFilters(): void {
    this.query.set('');
    this.kindFilter.set('all');
  }

  /** Clicking the open folder again clears the scope, so the tile acts as a toggle. */
  openSectionFolder(updatedByRole: string): void {
    this.openFolder.set(this.openFolder() === updatedByRole ? null : updatedByRole);
    this.selectedFile.set(null);
  }

  isFolderOpen(updatedByRole: string): boolean {
    return this.openFolder() === updatedByRole;
  }

  closeFolder(): void {
    this.openFolder.set(null);
    this.selectedFile.set(null);
  }

  /** Clicking the same column again flips direction, as the Drive list header does. */
  sortBy(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDir.set(this.sortDir() === 'asc' ? 'desc' : 'asc');
      return;
    }
    this.sortKey.set(key);
    // Names read best A→Z; dates and sizes read best largest / newest first.
    this.sortDir.set(key === 'Category' || key === 'Name' ? 'asc' : 'desc');
  }

  sortLabel(): string {
    return this.sortOptions.find((option) => option.value === this.sortKey())?.label ?? '';
  }

  kindFilterLabel(): string {
    return this.kindFilters.find((option) => option.value === this.kindFilter())?.label ?? 'All types';
  }

  select(row: MaintenanceAttachmentRow): void {
    this.selectedFile.set(this.selectedFile() === row ? null : row);
  }

  isSelected(row: MaintenanceAttachmentRow): boolean {
    return this.selectedFile() === row;
  }

  // ── Presentation helpers ───────────────────────────────────
  private kindOf(fileName: string): AttachmentKind {
    const name = fileName.toLowerCase();
    if (name.endsWith('.pdf')) {
      return 'pdf';
    }
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) {
      return 'image';
    }
    return 'other';
  }

  fileIcon(row: MaintenanceAttachmentRow): string {
    switch (row.kind) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'image':
        return 'image';
      default:
        return 'insert_drive_file';
    }
  }

  /** `report.final.pdf` → `PDF` — the badge shown on a grid tile. */
  fileExtension(row: MaintenanceAttachmentRow): string {
    const parts = row.fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase().slice(0, 4) : 'FILE';
  }


  // ── Preview panel ──────────────────────────────────────────
  /**
   * Opens the document in the preview backdrop. Files picked in this session are
   * read straight from the browser; a stored document needs a `previewUrl`, and
   * without one the panel says so and `previewStored` fires so the host can fetch
   * it once a document endpoint exists.
   */
  async view(row: MaintenanceAttachmentRow): Promise<void> {
    this.releaseObjectUrl();
    this.showPreview = true;
    try {

      const responseData = await this.maintenanceAllAttachmentsService
        .previewdocuments(Number(row.documentId));

      // Create blob URL
      const fileURL = URL.createObjectURL(responseData);

      // Safe URL
      this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
      this.previewRow = row;
      this.currentFileName = row.fileName;
      this.previewMode = this.previewModeFor(row.fileName);

    } catch (error) {
      console.error(error);
    }
    this.resetZoom();

  }

  closePreview(): void {
    this.showPreview = false;
    this.previewRow = null;
    this.releaseObjectUrl();
    this.resetZoom();
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.MAX_ZOOM, +(this.zoomLevel + this.ZOOM_STEP).toFixed(2));
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.MIN_ZOOM, +(this.zoomLevel - this.ZOOM_STEP).toFixed(2));
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  /** Only PDFs and images render inline; anything else is offered as a download. */
  private previewModeFor(fileName: string): PreviewMode {
    const name = fileName.toLowerCase();
    if (name.endsWith('.pdf')) {
      return 'pdf';
    }
    if (/\.(png|jpeg|gif|webp|bmp|svg)$/.test(name)) {
      return 'image';
    }
    return 'unsupported';
  }

  private releaseObjectUrl(): void {
    if (this.createdObjectUrl) {
      URL.revokeObjectURL(this.createdObjectUrl);
      this.createdObjectUrl = null;
    }
  }

  // ── Actions ────────────────────────────────────────────────
  async download(row: MaintenanceAttachmentRow): Promise<void> {
   const responseData = await this.maintenanceAllAttachmentsService
        .previewdocuments(Number(row.documentId));
    const url = URL.createObjectURL(responseData);
    const link = document.createElement('a');
    link.href = url;
    link.download = row.fileName;
    link.click();
    URL.revokeObjectURL(url);
  }
}
